import React, { useState, useRef, useEffect } from 'react';
import Cropper, { ReactCropperElement } from "react-cropper";
import { Rnd } from "react-rnd";
import "cropperjs/dist/cropper.css";
import { FileItem } from '../services/storageService';

interface ImageEditorProps {
    file: FileItem;
    onSave: (blob: Blob) => void;
    onCancel: () => void;
}

interface Filters {
    brightness: number;
    contrast: number;
    saturation: number;
    hue: number;
    blur: number;
    sepia: number;
    grayscale: number;
}

const DEFAULT_FILTERS: Filters = { brightness: 100, contrast: 100, saturation: 100, hue: 0, blur: 0, sepia: 0, grayscale: 0 };

// Fonction pure — hors composant pour éviter les recreations
function buildCSSFilter(f: Filters): string {
    const parts: string[] = [];
    if (f.brightness !== 100)  parts.push(`brightness(${f.brightness}%)`);
    if (f.contrast !== 100)    parts.push(`contrast(${f.contrast}%)`);
    if (f.saturation !== 100)  parts.push(`saturate(${f.saturation}%)`);
    if (f.hue !== 0)           parts.push(`hue-rotate(${f.hue}deg)`);
    if (f.blur !== 0)          parts.push(`blur(${f.blur}px)`);
    if (f.sepia !== 0)         parts.push(`sepia(${f.sepia}%)`);
    if (f.grayscale !== 0)     parts.push(`grayscale(${f.grayscale}%)`);
    return parts.length ? parts.join(' ') : 'none';
}

function buildTransform(fH: boolean, fV: boolean): string {
    if (!fH && !fV) return 'none';
    return `scaleX(${fH ? -1 : 1}) scaleY(${fV ? -1 : 1})`;
}

const ImageEditor: React.FC<ImageEditorProps> = ({ file, onSave, onCancel }) => {
    const cropperRef = useRef<ReactCropperElement>(null);
    const containerRef = useRef<HTMLDivElement>(null);

    const [activeTool, setActiveTool] = useState<'crop' | 'resize' | 'adjust'>('crop');

    const [inputWidth, setInputWidth] = useState<number>(0);
    const [inputHeight, setInputHeight] = useState<number>(0);
    const [rndWidth, setRndWidth] = useState<number>(0);
    const [rndHeight, setRndHeight] = useState<number>(0);
    const [rndX, setRndX] = useState<number>(0);
    const [rndY, setRndY] = useState<number>(0);
    const [maintainAspectRatio, setMaintainAspectRatio] = useState(true);
    const [isUserTyping] = useState(false);
    const [exportQuality, setExportQuality] = useState(92);

    // State pour les labels et boutons (affichage)
    const [filters, setFilters] = useState<Filters>({ ...DEFAULT_FILTERS });
    const [flipH, setFlipH] = useState(false);
    const [flipV, setFlipV] = useState(false);

    // Refs pour DOM direct (fluidité sliders sans re-render)
    const adjustImgRef = useRef<HTMLImageElement>(null);
    const filtersRef = useRef<Filters>({ ...DEFAULT_FILTERS });
    const flipHRef = useRef(false);
    const flipVRef = useRef(false);

    const isJpeg = !file.name.toLowerCase().endsWith('.png');
    const fileType = isJpeg ? 'image/jpeg' : 'image/png';
    const hasAdjustments = JSON.stringify(filters) !== JSON.stringify(DEFAULT_FILTERS) || flipH || flipV;

    // Applique filtres au DOM directement (sans passer par React)
    const applyToDOM = (f: Filters, fH: boolean, fV: boolean) => {
        if (!adjustImgRef.current) return;
        adjustImgRef.current.style.filter = buildCSSFilter(f);
        adjustImgRef.current.style.transform = buildTransform(fH, fV);
    };

    // Quand on entre en mode adjust, synchroniser le DOM avec les refs
    useEffect(() => {
        if (activeTool === 'adjust') {
            applyToDOM(filtersRef.current, flipHRef.current, flipVRef.current);
        }
    }, [activeTool]); // eslint-disable-line

    // Initialisation dimensions
    useEffect(() => {
        const img = new Image();
        img.src = file.url;
        img.onload = () => {
            setInputWidth(img.naturalWidth);
            setInputHeight(img.naturalHeight);
            if (containerRef.current) {
                const cW = containerRef.current.clientWidth;
                const cH = containerRef.current.clientHeight;
                const scale = Math.min((cW * 0.9) / img.naturalWidth, (cH * 0.9) / img.naturalHeight, 1);
                const vW = img.naturalWidth * scale;
                const vH = img.naturalHeight * scale;
                setRndWidth(vW);
                setRndHeight(vH);
                setRndX((cW - vW) / 2);
                setRndY((cH - vH) / 2);
            }
        };
    }, [file.url, activeTool]);

    const onCropReady = () => {
        if (activeTool === 'crop' && cropperRef.current?.cropper) {
            const d = cropperRef.current.cropper.getData();
            setInputWidth(Math.round(d.width));
            setInputHeight(Math.round(d.height));
        }
    };

    const onCrop = (event: any) => {
        if (activeTool !== 'crop' || isUserTyping) return;
        setInputWidth(Math.round(event.detail.width));
        setInputHeight(Math.round(event.detail.height));
    };

    const handleSave = () => {
        if (activeTool === 'crop') {
            cropperRef.current?.cropper.getCroppedCanvas({
                imageSmoothingQuality: 'high',
                fillColor: fileType === 'image/png' ? 'transparent' : '#fff'
            }).toBlob((b) => { if (b) onSave(b); }, fileType, exportQuality / 100);
            return;
        }

        if (activeTool === 'resize') {
            const canvas = document.createElement('canvas');
            canvas.width = inputWidth;
            canvas.height = inputHeight;
            const ctx = canvas.getContext('2d');
            const img = new Image();
            img.crossOrigin = 'anonymous';
            img.src = file.url;
            img.onload = () => {
                if (ctx) {
                    ctx.imageSmoothingEnabled = true;
                    ctx.imageSmoothingQuality = 'high';
                    ctx.drawImage(img, 0, 0, inputWidth, inputHeight);
                    canvas.toBlob((b) => { if (b) onSave(b); }, fileType, exportQuality / 100);
                }
            };
            return;
        }

        if (activeTool === 'adjust') {
            const img = new Image();
            img.crossOrigin = 'anonymous';
            img.src = file.url;
            img.onload = () => {
                const w = img.naturalWidth;
                const h = img.naturalHeight;
                const canvas = document.createElement('canvas');
                canvas.width = w;
                canvas.height = h;
                const ctx = canvas.getContext('2d');
                if (!ctx) return;
                const cssFilter = buildCSSFilter(filtersRef.current);
                if (cssFilter !== 'none') (ctx as any).filter = cssFilter;
                ctx.save();
                ctx.translate(flipHRef.current ? w : 0, flipVRef.current ? h : 0);
                ctx.scale(flipHRef.current ? -1 : 1, flipVRef.current ? -1 : 1);
                ctx.drawImage(img, 0, 0, w, h);
                ctx.restore();
                canvas.toBlob((b) => { if (b) onSave(b); }, fileType, exportQuality / 100);
            };
        }
    };

    const handleRndResize = (ref: HTMLElement) => {
        setRndWidth(ref.offsetWidth);
        setRndHeight(ref.offsetHeight);
        setInputWidth(Math.round(ref.offsetWidth));
        setInputHeight(Math.round(ref.offsetHeight));
    };

    const handleInputChange = (type: 'width' | 'height', value: number) => {
        const v = Math.max(1, value || 0);
        const ratio = rndHeight / rndWidth;
        if (type === 'width') {
            setInputWidth(v); setRndWidth(v);
            if (maintainAspectRatio) { const h = Math.round(v * ratio); setInputHeight(h); setRndHeight(h); }
        } else {
            setInputHeight(v); setRndHeight(v);
            if (maintainAspectRatio) { const w = Math.round(v / ratio); setInputWidth(w); setRndWidth(w); }
        }
    };

    const rotate = (deg: number) => { if (activeTool === 'crop') cropperRef.current?.cropper.rotate(deg); };

    // Mise à jour filtre : DOM direct (instantané) + state (labels)
    const setFilter = (key: keyof Filters, value: number) => {
        const next = { ...filtersRef.current, [key]: value };
        filtersRef.current = next;
        if (adjustImgRef.current) adjustImgRef.current.style.filter = buildCSSFilter(next);
        setFilters(next);
    };

    const handleFlipH = () => {
        const next = !flipHRef.current;
        flipHRef.current = next;
        if (adjustImgRef.current) adjustImgRef.current.style.transform = buildTransform(next, flipVRef.current);
        setFlipH(next);
    };

    const handleFlipV = () => {
        const next = !flipVRef.current;
        flipVRef.current = next;
        if (adjustImgRef.current) adjustImgRef.current.style.transform = buildTransform(flipHRef.current, next);
        setFlipV(next);
    };

    const resetFilters = () => {
        filtersRef.current = { ...DEFAULT_FILTERS };
        flipHRef.current = false;
        flipVRef.current = false;
        if (adjustImgRef.current) { adjustImgRef.current.style.filter = 'none'; adjustImgRef.current.style.transform = 'none'; }
        setFilters({ ...DEFAULT_FILTERS });
        setFlipH(false);
        setFlipV(false);
    };

    // Fonction de rendu slider (pas un composant React — pas de remount à chaque render)
    const renderSlider = (label: string, filterKey: keyof Filters, min: number, max: number, step = 1, unit = '') => (
        <div key={filterKey} style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 0 }}>
            <span style={{ fontSize: 11, color: '#64748b', whiteSpace: 'nowrap', width: 72, textAlign: 'right' }}>{label}</span>
            <input
                type="range" min={min} max={max} step={step}
                value={filters[filterKey]}
                onChange={e => setFilter(filterKey, parseFloat(e.target.value))}
                style={{ flex: 1, minWidth: 60, accentColor: '#6366f1' }}
            />
            <span style={{ fontSize: 11, color: '#1e293b', fontWeight: 600, width: 38, textAlign: 'left' }}>
                {filters[filterKey]}{unit}
            </span>
        </div>
    );

    return (
        <div style={{ position: 'fixed', inset: 0, zIndex: 99999, backgroundColor: '#000', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <style>{`.wrapper-crop .cropper-view-box{outline:2px solid #3b82f6}.wrapper-crop .cropper-point{background-color:#3b82f6}`}</style>

            <div className={`card shadow-lg wrapper-${activeTool}`} style={{ width: '90%', height: '90%', maxWidth: '1200px', display: 'flex', flexDirection: 'column' }}>
                <div className="card-header bg-white d-flex justify-content-between align-items-center py-2 px-3">
                    <h5 className="mb-0 text-muted"><i className="fas fa-edit me-2"></i>Éditeur d'image</h5>
                    <button className="btn-close" onClick={onCancel}></button>
                </div>

                <div ref={containerRef} className="card-body p-0 position-relative overflow-hidden flex-grow-1 d-flex flex-column" style={{ backgroundColor: '#111' }}>

                    {activeTool === 'crop' && (
                        <Cropper src={file.url} style={{ height: '100%', width: '100%' }} guides ref={cropperRef}
                            viewMode={1} dragMode="move" crossOrigin="anonymous" background={false}
                            responsive autoCropArea={0.9} ready={onCropReady} crop={onCrop} />
                    )}

                    {activeTool === 'resize' && (
                        <div style={{ width: '100%', height: '100%', overflow: 'auto', display: 'flex', alignItems: 'flex-start', justifyContent: 'flex-start', position: 'relative' }}>
                            <Rnd size={{ width: rndWidth, height: rndHeight }} position={{ x: rndX, y: rndY }}
                                onDragStop={(e, d) => { setRndX(d.x); setRndY(d.y); }}
                                onResize={(e, dir, ref, delta, pos) => { setRndX(pos.x); setRndY(pos.y); handleRndResize(ref); }}
                                lockAspectRatio={maintainAspectRatio} bounds="parent"
                                style={{ border: '1px solid #f97316', zIndex: 20 }}
                                enableResizing={{ top: true, right: true, bottom: true, left: true, topRight: true, bottomRight: true, bottomLeft: true, topLeft: true }}>
                                <img src={file.url} style={{ width: '100%', height: '100%', display: 'block', pointerEvents: 'none' }} draggable={false} />
                                <div className="position-absolute bottom-0 end-0 bg-warning text-dark px-2 fw-bold" style={{ fontSize: 10, opacity: 0.8 }}>
                                    {Math.round(inputWidth)} x {Math.round(inputHeight)}
                                </div>
                            </Rnd>
                        </div>
                    )}

                    {activeTool === 'adjust' && (
                        <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}>
                            <img ref={adjustImgRef} src={file.url}
                                style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
                                draggable={false} />
                        </div>
                    )}

                    <div className="position-absolute top-0 start-0 m-3 badge rounded-pill shadow"
                        style={{ backgroundColor: activeTool === 'crop' ? '#3b82f6' : activeTool === 'resize' ? '#f97316' : '#6366f1', fontSize: '0.9rem', padding: '0.75em 1.25em', zIndex: 30 }}>
                        <i className={`fas ${activeTool === 'crop' ? 'fa-crop-alt' : activeTool === 'resize' ? 'fa-expand' : 'fa-sliders-h'} me-2`}></i>
                        {activeTool === 'crop' ? 'Recadrage' : activeTool === 'resize' ? 'Redimensionnement' : 'Ajustements'}
                    </div>
                </div>

                <div className="card-footer bg-light py-2 px-3">

                    {activeTool === 'adjust' && (
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: '4px 20px', marginBottom: 10, padding: '8px 0', borderBottom: '1px solid #e2e8f0' }}>
                            {renderSlider('Luminosité', 'brightness', 0, 200, 1, '%')}
                            {renderSlider('Contraste',  'contrast',   0, 200, 1, '%')}
                            {renderSlider('Saturation', 'saturation', 0, 300, 1, '%')}
                            {renderSlider('Teinte',     'hue',        0, 360, 1, '°')}
                            {renderSlider('Flou',       'blur',       0, 10, 0.5, 'px')}
                            {renderSlider('Sépia',      'sepia',      0, 100, 1, '%')}
                            {renderSlider('N&B',        'grayscale',  0, 100, 1, '%')}
                        </div>
                    )}

                    <div className="row g-2 align-items-center">
                        <div className="col-auto border-end pe-3">
                            <div className="btn-group shadow-sm">
                                <button className={`btn btn-sm ${activeTool === 'crop' ? 'btn-primary' : 'btn-outline-primary'}`} onClick={() => setActiveTool('crop')}>
                                    <i className="fas fa-crop-alt me-1"></i>Recadrer
                                </button>
                                <button className={`btn btn-sm ${activeTool === 'resize' ? 'btn-warning text-white' : 'btn-outline-warning text-dark'}`} onClick={() => setActiveTool('resize')}>
                                    <i className="fas fa-expand me-1"></i>Resize
                                </button>
                                <button className={`btn btn-sm ${activeTool === 'adjust' ? 'text-white' : 'btn-outline-secondary'}`}
                                    style={activeTool === 'adjust' ? { backgroundColor: '#6366f1', borderColor: '#6366f1', color: '#fff' } : {}}
                                    onClick={() => setActiveTool('adjust')}>
                                    <i className="fas fa-sliders-h me-1"></i>Ajuster
                                </button>
                            </div>
                        </div>

                        {activeTool !== 'adjust' && (
                            <div className="col-auto d-flex align-items-center gap-2 border-end pe-3">
                                <div className="input-group input-group-sm" style={{ width: 240 }}>
                                    <input type="number" className="form-control text-center fw-bold" value={Math.round(inputWidth) || ''} onChange={e => handleInputChange('width', parseInt(e.target.value))} />
                                    <span className="input-group-text">x</span>
                                    <input type="number" className="form-control text-center fw-bold" value={Math.round(inputHeight) || ''} onChange={e => handleInputChange('height', parseInt(e.target.value))} />
                                </div>
                                <button className={`btn btn-sm ${maintainAspectRatio ? 'btn-dark' : 'btn-outline-secondary'}`} onClick={() => setMaintainAspectRatio(p => !p)}>
                                    <i className={`fas ${maintainAspectRatio ? 'fa-link' : 'fa-unlink'}`}></i>
                                </button>
                            </div>
                        )}

                        {activeTool === 'adjust' && (
                            <div className="col-auto d-flex align-items-center gap-1 border-end pe-3">
                                <button className={`btn btn-sm ${flipH ? 'btn-dark' : 'btn-outline-secondary'}`} onClick={handleFlipH} title="Miroir horizontal">
                                    <i className="fas fa-arrows-alt-h"></i>
                                </button>
                                <button className={`btn btn-sm ${flipV ? 'btn-dark' : 'btn-outline-secondary'}`} onClick={handleFlipV} title="Miroir vertical">
                                    <i className="fas fa-arrows-alt-v"></i>
                                </button>
                                {hasAdjustments && (
                                    <button className="btn btn-sm btn-outline-danger" onClick={resetFilters} title="Réinitialiser">
                                        <i className="fas fa-undo"></i>
                                    </button>
                                )}
                            </div>
                        )}

                        <div className="col-auto me-auto">
                            {activeTool === 'crop' && (
                                <div className="btn-group btn-group-sm">
                                    <button className="btn btn-outline-secondary" onClick={() => rotate(-90)}><i className="fas fa-undo"></i></button>
                                    <button className="btn btn-outline-secondary" onClick={() => rotate(90)}><i className="fas fa-redo"></i></button>
                                </div>
                            )}
                        </div>

                        {isJpeg && (
                            <div className="col-auto d-flex align-items-center gap-2 border-start ps-3">
                                <span style={{ fontSize: 11, color: '#64748b', whiteSpace: 'nowrap' }}>Qualité</span>
                                <input type="range" min={50} max={100} value={exportQuality} onChange={e => setExportQuality(parseInt(e.target.value))} style={{ width: 80, accentColor: '#10b981' }} />
                                <span style={{ fontSize: 11, fontWeight: 600, width: 32 }}>{exportQuality}%</span>
                            </div>
                        )}

                        <div className="col-auto">
                            <button className="btn btn-sm btn-light border me-2" onClick={onCancel}>Annuler</button>
                            <button className="btn btn-sm btn-success px-4 fw-bold" onClick={handleSave}>
                                <i className="fas fa-save me-2"></i>Enregistrer
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ImageEditor;
